import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { changePassword } from '../utils/apiServices';
import { AuthContext } from '../contexts/AuthContext';

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState('');
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [loading, user, navigate]);

  const validatePasswords = () => {
    if (newPassword.length < 6) {
      setErrors('La contraseña debe tener al menos 6 caracteres.');
      return false;
    }
    if (newPassword !== confirmNewPassword) {
      setErrors('Las contraseñas nuevas no coinciden.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validatePasswords()) {
      setMessage('');
      return;
    }

    try {
      await changePassword(user.token, currentPassword, newPassword);
      setMessage('Tu contraseña ha sido cambiada exitosamente.');
      setErrors('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      setErrors(error.response?.data?.detail || 'Hubo un error al cambiar la contraseña. Inténtalo de nuevo.');
      setMessage('');
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #f4f7fc 0%, #e8f0fe 100%)' }}
      >
        <div
          className="border-4 rounded-full animate-spin"
          style={{
            width: '60px',
            height: '60px',
            borderColor: '#e8f0fe',
            borderTopColor: 'var(--color-azul-icetex)',
          }}
        ></div>
        <p className="text-texto-sec font-semibold mt-5 text-[1.1rem]">Cargando...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 py-[40px]"
      style={{ background: 'linear-gradient(135deg, #f4f7fc 0%, #e8f0fe 100%)' }}
    >
      <div className="w-full animate-fade-in" style={{ maxWidth: '600px' }}>

        {/* Header */}
        <div className="mb-[30px]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-transparent text-icetex border-0 font-semibold cursor-pointer no-underline px-5 py-3 rounded-full text-base transition-all duration-300 hover:bg-white hover:-translate-x-1 hover:shadow-[0_4px_15px_rgba(0,63,129,0.2)]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            Volver al Inicio
          </Link>
        </div>

        {/* Card */}
        <div
          className="bg-white text-center rounded-[30px] px-[50px] py-[60px] shadow-[0_20px_60px_rgba(0,63,129,0.15)] max-[768px]:px-[30px] max-[768px]:py-[40px] max-[768px]:rounded-[20px] max-[480px]:px-5 max-[480px]:py-[30px]"
        >
          {/* Icon */}
          <div
            className="flex items-center justify-center mx-auto mb-[30px] rounded-full shadow-[0_10px_30px_rgba(0,63,129,0.3)]"
            style={{
              width: '100px',
              height: '100px',
              background: 'linear-gradient(135deg, var(--color-azul-icetex) 0%, #0056b3 100%)',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="50"
              height="50"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-white max-[480px]:w-10 max-[480px]:h-10"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>

          <h1 className="text-texto-oscuro font-extrabold text-[2.2rem] mb-[15px] max-[768px]:text-[1.8rem] max-[480px]:text-[1.5rem]">
            Cambiar Contraseña
          </h1>
          <p className="text-texto-sec text-[1.05rem] leading-relaxed mb-[40px] max-[768px]:text-base">
            Actualiza tu contraseña para mantener tu cuenta segura
          </p>

          <form onSubmit={handleSubmit} className="text-left">

            {/* Current Password */}
            <div className="mb-[25px]">
              <label
                htmlFor="currentPassword"
                className="flex items-center gap-2 text-texto-oscuro font-bold uppercase text-[0.95rem] mb-[10px] tracking-[0.5px]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-icetex"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Contraseña Actual
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full outline-none px-5 py-4 border-2 border-[#e8f0fe] rounded-[15px] text-base bg-[#f8fafc] transition-all duration-300 placeholder:text-slate-400 focus:border-icetex focus:bg-white focus:shadow-[0_0_0_4px_rgba(0,63,129,0.1)] max-[480px]:px-4 max-[480px]:py-[14px] max-[480px]:text-[0.95rem]"
                placeholder="Ingresa tu contraseña actual"
              />
            </div>

            {/* New Password */}
            <div className="mb-[25px]">
              <label
                htmlFor="newPassword"
                className="flex items-center gap-2 text-texto-oscuro font-bold uppercase text-[0.95rem] mb-[10px] tracking-[0.5px]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-icetex"
                >
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                </svg>
                Nueva Contraseña
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full outline-none px-5 py-4 border-2 border-[#e8f0fe] rounded-[15px] text-base bg-[#f8fafc] transition-all duration-300 placeholder:text-slate-400 focus:border-icetex focus:bg-white focus:shadow-[0_0_0_4px_rgba(0,63,129,0.1)] max-[480px]:px-4 max-[480px]:py-[14px] max-[480px]:text-[0.95rem]"
                placeholder="Ingresa tu nueva contraseña"
              />
            </div>

            {/* Confirm New Password */}
            <div className="mb-[25px]">
              <label
                htmlFor="confirmNewPassword"
                className="flex items-center gap-2 text-texto-oscuro font-bold uppercase text-[0.95rem] mb-[10px] tracking-[0.5px]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-icetex"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Confirmar Nueva Contraseña
              </label>
              <input
                type="password"
                id="confirmNewPassword"
                name="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                className="w-full outline-none px-5 py-4 border-2 border-[#e8f0fe] rounded-[15px] text-base bg-[#f8fafc] transition-all duration-300 placeholder:text-slate-400 focus:border-icetex focus:bg-white focus:shadow-[0_0_0_4px_rgba(0,63,129,0.1)] max-[480px]:px-4 max-[480px]:py-[14px] max-[480px]:text-[0.95rem]"
                placeholder="Confirma tu nueva contraseña"
              />
            </div>

            {/* Error Alert */}
            {errors && (
              <div className="flex items-center gap-3 px-5 py-4 bg-[#fee] border-2 border-[#fcc] rounded-[15px] mb-[25px] animate-shake">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 text-[#c00]"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p className="m-0 text-[#c00] text-[0.95rem] font-semibold">{errors}</p>
              </div>
            )}

            {/* Success Alert */}
            {message && (
              <div className="flex items-center gap-3 px-5 py-4 bg-[#d4edda] border-2 border-[#c3e6cb] rounded-[15px] mb-[25px] animate-fade-in">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 text-[#155724]"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <p className="m-0 text-[#155724] text-[0.95rem] font-semibold">{message}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full text-white border-0 font-bold uppercase cursor-pointer py-[18px] rounded-full text-[1.05rem] tracking-[0.5px] shadow-[0_6px_20px_rgba(0,63,129,0.3)] mt-[10px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,63,129,0.4)] active:translate-y-0"
              style={{ background: 'linear-gradient(135deg, var(--color-azul-icetex) 0%, #0056b3 100%)' }}
            >
              Actualizar Contraseña
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-[30px] pt-5">
          <p className="text-texto-sec text-[0.9rem]">Plataforma de Herramientas Digitales - ICETEX</p>
        </div>

      </div>
    </div>
  );
}

export default ChangePassword;
